import { useEffect } from 'react';
import Navbar from '../../../components/Navbar';
import Footer from '../../home/components/Footer';

const safetyServices = [
  {
    emoji: '📋',
    name: 'EICR Testing',
    slug: 'eicr-testing',
    description: 'Comprehensive electrical installation condition reports for homes and businesses in Watford'
  },
  {
    emoji: '🏠',
    name: 'Landlord Safety Certificates',
    slug: 'landlord-safety-certificates',
    description: 'Legal compliance certificates for landlords across Watford and surrounding areas'
  },
  {
    emoji: '🔍',
    name: 'Homebuyer Electrical Reports',
    slug: 'homebuyer-electrical-reports',
    description: 'Pre-purchase electrical surveys to protect your property investment in Watford'
  },
  {
    emoji: '🚨',
    name: 'Smoke & Heat Alarm Installation',
    slug: 'smoke-heat-alarm',
    description: 'Professional smoke and heat detector installation for maximum safety in Watford homes'
  },
  {
    emoji: '🔔',
    name: 'Fire Alarm Systems',
    slug: 'fire-alarm-systems',
    description: 'Complete fire alarm system design, installation and maintenance in Watford'
  },
  {
    emoji: '⚡',
    name: 'Surge Protection',
    slug: 'surge-protection',
    description: 'Protect your valuable electronics and appliances from power surges in Watford'
  },
  {
    emoji: '🔗',
    name: 'Earthing & Bonding',
    slug: 'earthing-bonding',
    description: 'Essential earthing and bonding services for electrical safety in Watford properties'
  },
  {
    emoji: '✅',
    name: 'RCD Testing',
    slug: 'rcd-testing',
    description: 'Regular RCD testing to ensure your electrical protection devices work correctly'
  }
];

const whyChooseReasons = [
  {
    icon: 'ri-shield-check-line',
    title: 'NICEIC Approved',
    description: 'All work certified to BS 7671 standards with full compliance documentation'
  },
  {
    icon: 'ri-time-line',
    title: 'Fast Response',
    description: 'Same-day appointments available for urgent safety inspections in Watford'
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Detailed Reports',
    description: 'Comprehensive reports with clear recommendations and compliance certificates'
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Expert Advice',
    description: 'Professional guidance on all electrical safety and compliance matters'
  }
];

const watfordAreas = [
  'Watford Town Centre', 'Oxhey', 'Bushey', 'Croxley Green', 'Rickmansworth',
  'Abbots Langley', 'Kings Langley', 'Garston', 'Leavesden', 'Carpenders Park',
  'Borehamwood', 'Radlett', 'Chorleywood', 'Sarratt', 'Chipperfield'
];

export default function SafetyCompliancePage() {
  useEffect(() => {
    document.title = 'Electrical Safety & Compliance Watford | Beyond Electrical Services';

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Electrical safety testing & compliance in Watford. EICR testing, landlord certificates, RCD testing, fire alarms & more. NICEIC approved contractor. Call +44 7989 675952.');
    }

    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      (metaKeywords as HTMLMetaElement).name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    (metaKeywords as HTMLMetaElement).content = 'electrical safety Watford, EICR testing Watford, landlord certificate Watford, RCD testing Watford, fire alarm Watford, NICEIC compliance Watford';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      (canonical as HTMLLinkElement).rel = 'canonical';
      document.head.appendChild(canonical);
    }
    (canonical as HTMLLinkElement).href = `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/safety-compliance`;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'safety-schema';
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Beyond Electrical Services",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/safety-compliance`,
        "telephone": "+447989675952",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Watford",
          "addressRegion": "Hertfordshire",
          "addressCountry": "GB"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": 51.6565,
          "longitude": -0.3957
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "priceRange": "££"
      },
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Electrical Safety & Compliance Services Watford",
        "description": "Professional electrical safety testing, inspections and compliance certificates for homes and businesses across Watford. NICEIC approved contractors.",
        "provider": {
          "@type": "LocalBusiness",
          "name": "Beyond Electrical Services",
          "telephone": "+447989675952"
        },
        "areaServed": { "@type": "City", "name": "Watford" },
        "serviceType": "Electrical Safety & Compliance",
        "url": `${import.meta.env.VITE_SITE_URL || 'https://beyondelectrical.co.uk'}/services/safety-compliance`
      }
    ]);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('safety-schema');
      if (s) document.head.removeChild(s);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-[#0A1628] via-[#0D1F3C] to-[#0A1628] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#FFD700] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFD700] rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
              <div className="flex items-center gap-2 bg-[#FFD700]/20 border border-[#FFD700] text-[#FFD700] px-4 py-2 rounded-full text-sm font-semibold">
                <i className="ri-shield-check-line text-lg"></i>
                NICEIC Approved Contractor
              </div>
              <div className="flex items-center gap-2 bg-white/10 border border-white/30 text-white px-4 py-2 rounded-full text-sm font-semibold">
                <i className="ri-map-pin-line text-lg"></i>
                Serving Watford &amp; Surrounding Areas
              </div>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Safety &amp; Compliance Services
              <span className="block text-[#FFD700] mt-2">in Watford</span>
            </h1>

            <p className="text-lg sm:text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              Professional electrical safety testing, inspections and compliance certificates for homes and businesses across Watford. NICEIC approved contractors ensuring your property meets all legal requirements.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+447989675952"
                className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-lg shadow-[#FFD700]/30 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-xl"></i>
                Call +44 7989 675952
              </a>
              <a
                href="/#contact"
                className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Get Free Quote
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628] mb-4">
              Our Safety &amp; Compliance Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive electrical safety testing and certification services to keep your Watford property compliant and safe
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {safetyServices.map((service) => (
              <a
                key={service.slug}
                href={`/services/safety-compliance/${service.slug}`}
                className="group bg-gradient-to-br from-gray-50 to-white border-2 border-gray-200 rounded-2xl p-6 hover:border-[#FFD700] hover:shadow-xl hover:shadow-[#FFD700]/20 transition-all duration-300 cursor-pointer"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#FFD700] to-[#FFC700] rounded-2xl flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                  {service.emoji}
                </div>
                <h3 className="text-xl font-bold text-[#0A1628] mb-3 group-hover:text-[#FFD700] transition-colors duration-300">
                  {service.name}
                </h3>
                <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                  {service.description}
                </p>
                <div className="flex items-center text-[#FFD700] font-semibold text-sm group-hover:gap-2 transition-all duration-300">
                  Learn More
                  <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform duration-300"></i>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0D1F3C] to-[#0A1628]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Choose Beyond Electrical?
            </h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              Trusted electrical safety experts serving Watford with professional testing and certification services
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whyChooseReasons.map((reason, index) => (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-[#FFD700]/50 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-[#FFD700]/20 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${reason.icon} text-3xl text-[#FFD700]`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-2xl p-8 text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <i className="ri-shield-star-line text-4xl text-[#FFD700]"></i>
              <h3 className="text-2xl font-bold text-white">Fully Certified &amp; Insured</h3>
            </div>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              All our electrical safety work is carried out by NICEIC approved electricians with full public liability insurance and Part P certification
            </p>
          </div>
        </div>
      </section>

      {/* Areas Covered */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A1628] mb-4">
              Areas We Cover in Watford
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Professional electrical safety services across Watford and surrounding areas
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-3">
            {watfordAreas.map((area, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 px-5 py-2.5 rounded-full text-sm font-semibold text-[#0A1628] hover:border-[#FFD700] hover:bg-[#FFD700]/10 hover:text-[#FFD700] transition-all duration-300 cursor-pointer"
              >
                {area}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#0A1628] via-[#0D1F3C] to-[#0A1628]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <i className="ri-flashlight-fill text-[#FFD700] text-3xl"></i>
            <span className="text-[#FFD700] font-bold text-lg">Need Safety Testing or Certification?</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
            Get Your Free Quote Today
          </h2>

          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Contact our NICEIC approved team for professional electrical safety testing and compliance certificates in Watford
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+447989675952"
              className="w-full sm:w-auto bg-[#FFD700] text-[#0A1628] px-8 py-4 rounded-full font-bold text-lg hover:bg-[#FFC700] transition-all duration-300 shadow-lg shadow-[#FFD700]/30 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill text-xl"></i>
              Call +44 7989 675952
            </a>
            <a
              href="/#contact"
              className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-[#0A1628] transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line text-xl"></i>
              Request Callback
            </a>
          </div>

          <div className="mt-10 flex items-center justify-center gap-6 text-gray-300 text-sm flex-wrap">
            <div className="flex items-center gap-2">
              <i className="ri-time-line text-[#FFD700]"></i>
              <span>Same-Day Service Available</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-[#FFD700]"></i>
              <span>NICEIC Approved</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-star-fill text-[#FFD700]"></i>
              <span>5-Star Rated</span>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}